import { memo, useCallback, useEffect, useMemo } from "react"
import { OrderBook_OrderBookQuote, Security } from '../../proto/generated/security'
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material"
import { useStyles } from "./styles"
import { getSecurityKey } from "../../util/security"
import { selectOrderBook } from "../../selectors/security"
import { useSelector } from "react-redux"
import { toLocaleStringIn } from "../../util/general"
import { useDispatch } from "react-redux"
import { setQuickOrderData } from "../../reducer/orders.reducer"
import { Broker_OrderType, OrderType, Product, TransactionType } from "../../proto/generated/order"

export type MarketDepthProps = {
    security: Security,
    bidAskCentered?: boolean,
    tradeable?: boolean
}

const MarketDepth: React.FC<MarketDepthProps> = ({ security, bidAskCentered = false, tradeable = true }) => {

    const dispatch = useDispatch();
    const { classes, cx } = useStyles();

    const securityKey = useMemo(() => security ? getSecurityKey(security.exchange, security.code) : '', [security])
    const orderBook = useSelector(selectOrderBook(securityKey))

    const onRowClick = useCallback((type: "bid" | "ask", price: number) => {
        dispatch(setQuickOrderData({
            openDialog: true,
            mode: 'regular',
            type: 'trade',
            isSquareOffOrder: false,
            order: {
                transactionType: type === "bid" ? TransactionType.SELL : TransactionType.BUY,
                security: security,
                editOrder: undefined,
                brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                orderType: OrderType.NORMAL,
                product: Product.CNC,
                price: price,
                quantity: 1 * (security.lotSize??1),
                maxQuantity: 0
            }
        }))
    }, [security])

    const maxBidQuantity = useMemo(() => {
        if (!orderBook || orderBook.bids?.length == 0) return 0;
        return orderBook.bids.reduce(
            (max , bid) => Math.max(max, bid.quantity), 0
        ) ;
    }, [orderBook?.bids])

    const bids = useMemo(() => {
        if (!orderBook) return [];
        const bidRows = orderBook.bids.length > 0
            ? orderBook.bids.map((bid, i) => {
                const shadowWidth = maxBidQuantity > 0 ? (bid.quantity / maxBidQuantity) * 100 : 0;
                let cells = [
                    <TableCell key="price" className={cx(classes.rowCell, classes.bidCell)} >{toLocaleStringIn(bid.price / (security?.priceMultiplier ?? 100))}</TableCell>,
                    <TableCell key="orders" className={cx(classes.rowCell, classes.bidCell)} >{bid.numberOfOrders}</TableCell>,
                    <TableCell key="qty" className={cx(classes.rowCell, classes.bidCell)}>
                        <div style={{ paddingLeft: 8, height: '100%' }}>
                            <div
                                style={{
                                    height: '100%',
                                    background: `linear-gradient(
                                        to left,
                                        rgba(65, 132, 243, 0.1) ${shadowWidth}%,
                                        transparent ${shadowWidth}%
                                    )`
                                }}
                            >
                                {bid.quantity}
                            </div>
                        </div>
                    </TableCell>
                ]
                return <TableRow key={`bid-${i}`} className={cx(classes.tableRow, { [classes.tradeable]: tradeable })} onClick={tradeable ? () => onRowClick("bid", bid.price) : undefined}>
                    {bidAskCentered ? cells.reverse() : cells}
                </TableRow>
            })
            : Array.from({ length: 5 }, (_, index) => {
                let cells = [
                    <TableCell key="price" className={cx(classes.rowCell, classes.bidCell)}>0.00</TableCell>,
                    <TableCell key="orders" className={cx(classes.rowCell, classes.bidCell)}>0</TableCell>,
                    <TableCell key="qty" className={cx(classes.rowCell, classes.bidCell)}>0</TableCell>
                ]
                return <TableRow key={`empty-bid-${index}`} className={cx(classes.tableRow, { [classes.tradeable]: tradeable })} onClick={tradeable ? () => onRowClick("bid", 0) : undefined}>
                    {bidAskCentered ? cells.reverse() : cells}
                </TableRow>
            });
        
        return bidRows.concat(<TableRow key="bid-total" className={classes.tableRow}>
            <TableCell className={cx(classes.rowCell, classes.bidCell)}>Total</TableCell>
            <TableCell colSpan={2} className={cx(classes.rowCell, classes.bidCell)}>{orderBook.totalBuyQuantity}</TableCell>
        </TableRow>)
    }, [orderBook, bidAskCentered, tradeable, security, maxBidQuantity])

    const maxAskQuantity = useMemo(() => {
        if (!orderBook || orderBook.asks?.length == 0) return 0;
        return orderBook.asks.reduce(
            (max , ask) => Math.max(max, ask.quantity), 0
        ) ;
    }, [orderBook?.asks])

    const asks = useMemo(() => {
        if (!orderBook) return [];
        
        const askRows = orderBook.asks.length > 0 
            ? orderBook.asks.map((ask, i) => {
                const shadowWidth = maxAskQuantity > 0 ? (ask.quantity / maxAskQuantity) * 100 : 0;
                return <TableRow key={`ask-${i}`} className={cx(classes.tableRow, { [classes.tradeable]: tradeable })} onClick={tradeable ? () => onRowClick("ask", ask.price) : undefined}>
                    <TableCell key="price" className={cx(classes.rowCell, classes.askCell)} >{toLocaleStringIn(ask.price / (security?.priceMultiplier ?? 100))}</TableCell>
                    <TableCell key="orders" className={cx(classes.rowCell, classes.askCell)} >{ask.numberOfOrders}</TableCell>
                    <TableCell key="qty" className={cx(classes.rowCell, classes.askCell)}>
                        <div style={{ paddingLeft: 8, height: '100%' }}>
                            <div
                            style={{
                                height: '100%',
                                background: `linear-gradient(
                                to left,
                                rgba(255, 87, 34, 0.1) ${shadowWidth}%,
                                transparent ${shadowWidth}%
                                )`
                            }}
                            >
                            {ask.quantity}
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            })
            : Array.from({ length: 5 }, (_, index) => (
                <TableRow key={`empty-ask-${index}`} className={cx(classes.tableRow, { [classes.tradeable]: tradeable })} onClick={tradeable ? () => onRowClick("ask", 0) : undefined}>
                    <TableCell key="price" className={cx(classes.rowCell, classes.askCell)}>0.00</TableCell>
                    <TableCell key="orders" className={cx(classes.rowCell, classes.askCell)}>0</TableCell>
                    <TableCell key="qty" className={cx(classes.rowCell, classes.askCell)}>0</TableCell>
                </TableRow>
            ));
        
        return askRows.concat(<TableRow key="ask-total" className={classes.tableRow}>
            <TableCell className={cx(classes.rowCell, classes.askCell)}>Total</TableCell>
            <TableCell colSpan={2} className={cx(classes.rowCell, classes.askCell)}>{orderBook.totalSellQuantity}</TableCell>
        </TableRow>)
    }, [orderBook, bidAskCentered, tradeable, security, maxAskQuantity])

    const bidHeaders = useMemo(() => {
        const headers = [
            <TableCell key="bids" className={classes.headerCell} >
                <Typography variant={'caption'} >BIDS</Typography>
            </TableCell>,
            <TableCell key="orders" className={classes.headerCell}>
                <Typography variant={'caption'} >ORDERS</Typography>
            </TableCell>,
            <TableCell key="qty" className={classes.headerCell}>
                <Typography variant={'caption'} >QTY</Typography>
            </TableCell>
        ];
        return bidAskCentered ? headers.reverse() : headers;
    }, [bidAskCentered, classes.headerCell])

    const askHeaders = useMemo(() => {
        return [
            <TableCell key="offers" className={classes.headerCell} >
                <Typography variant={'caption'} >OFFERS</Typography>
            </TableCell>,
            <TableCell key="orders" className={classes.headerCell}>
                <Typography variant={'caption'} >ORDERS</Typography>
            </TableCell>,
            <TableCell key="qty" className={classes.headerCell}>
                <Typography variant={'caption'} >QTY</Typography>
            </TableCell>
        ];
    }, [classes.headerCell])


    return <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
        <Box width="50%">
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        {bidHeaders}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {bids}
                </TableBody>
            </Table>
        </Box>
        <Box width="50%">
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        {askHeaders}
                    </TableRow>
                </TableHead>
                <TableBody>{asks}</TableBody>
            </Table>
        </Box>
    </Box>
}


export default memo(MarketDepth);